import React, { useState, useEffect } from 'react'
import InfoCard from '../../components/shared/InfoCard'
import { MarketCustomColumnsService } from '../../services/market-custom-columns.service'
import { MarketCustomColumn } from '../../types/database'

interface CustomColumnsSectionProps {
  modelId: string
}

export default function CustomColumnsSection({ modelId }: CustomColumnsSectionProps) {
  const [customColumns, setCustomColumns] = useState<MarketCustomColumn[]>([])
  const [loadingCustomColumns, setLoadingCustomColumns] = useState(false)
  const [customColumnForms, setCustomColumnForms] = useState<{
    [key: number]: {
      column_title: string
      input_type: 'text' | 'dropdown'
      options: string[]
    }
  }>({
    1: { column_title: '', input_type: 'text', options: [] },
    2: { column_title: '', input_type: 'text', options: [] },
    3: { column_title: '', input_type: 'text', options: [] }
  })

  useEffect(() => {
    fetchCustomColumns()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId])

  const fetchCustomColumns = async () => {
    try {
      setLoadingCustomColumns(true)
      const modelIdNum = parseInt(modelId, 10)
      const columnsData = await MarketCustomColumnsService.getByModelId(modelIdNum)
      setCustomColumns(columnsData)

      const newForms = { ...customColumnForms }
      columnsData.forEach(column => {
        newForms[column.column_index] = {
          column_title: column.column_title,
          input_type: column.input_type,
          options: column.options || []
        }
      })
      setCustomColumnForms(newForms)
    } catch (err: any) {
      // Error fetching custom columns
    } finally {
      setLoadingCustomColumns(false)
    }
  }

  const handleCustomColumnFormChange = (
    columnIndex: 1 | 2 | 3,
    field: 'column_title' | 'input_type',
    value: string
  ) => {
    setCustomColumnForms(prev => ({
      ...prev,
      [columnIndex]: {
        ...prev[columnIndex],
        [field]: value,
        options: field === 'input_type' && value === 'text' ? [] : prev[columnIndex].options
      }
    }))
  }

  const handleCustomColumnOptionsChange = (columnIndex: 1 | 2 | 3, options: string[]) => {
    setCustomColumnForms(prev => ({
      ...prev,
      [columnIndex]: {
        ...prev[columnIndex],
        options
      }
    }))
  }

  const handleSaveCustomColumn = async (columnIndex: 1 | 2 | 3) => {
    try {
      const form = customColumnForms[columnIndex]
      const validation = MarketCustomColumnsService.validateCustomColumn({
        model_id: parseInt(modelId, 10),
        column_index: columnIndex,
        column_title: form.column_title,
        input_type: form.input_type,
        options: form.input_type === 'dropdown' ? form.options : null
      })

      if (!validation.isValid) {
        alert('Validation errors:\n' + validation.errors.join('\n'))
        return
      }

      await MarketCustomColumnsService.upsert({
        model_id: parseInt(modelId, 10),
        column_index: columnIndex,
        column_title: form.column_title,
        input_type: form.input_type,
        options: form.input_type === 'dropdown' ? form.options : null
      })

      await fetchCustomColumns()
      alert('Custom column saved successfully!')
    } catch (err: any) {
      alert('Failed to save custom column: ' + err.message)
    }
  }

  const handleDeleteCustomColumn = async (columnIndex: 1 | 2 | 3) => {
    if (!window.confirm('Are you sure you want to delete this custom column? This will remove all data for this column.')) return

    try {
      await MarketCustomColumnsService.deleteByModelAndIndex(parseInt(modelId, 10), columnIndex)
      setCustomColumnForms(prev => ({
        ...prev,
        [columnIndex]: { column_title: '', input_type: 'text', options: [] }
      }))
      await fetchCustomColumns()
      alert('Custom column deleted successfully!')
    } catch (err: any) {
      alert('Failed to delete custom column: ' + err.message)
    }
  }

  return (
    <InfoCard title="Custom Columns">
      <div className="space-y-6">
        <p className="text-sm text-gray-600">
          Configure up to 3 custom data fields for this aircraft market. These fields will appear in market tables, aircraft details, and PDF exports.
        </p>

        {loadingCustomColumns ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-950"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {[1, 2, 3].map((columnIndex) => {
              const form = customColumnForms[columnIndex as 1 | 2 | 3]
              const existingColumn = customColumns.find(col => col.column_index === columnIndex)

              return (
                <div key={columnIndex} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-medium text-gray-900">Custom Column {columnIndex}</h4>
                    {existingColumn && (
                      <button
                        onClick={() => handleDeleteCustomColumn(columnIndex as 1 | 2 | 3)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Column Title</label>
                      <input
                        type="text"
                        value={form.column_title}
                        onChange={(e) => handleCustomColumnFormChange(
                          columnIndex as 1 | 2 | 3,
                          'column_title',
                          e.target.value
                        )}
                        placeholder="e.g., Interior Color, Avionics Package"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Input Type</label>
                      <select
                        value={form.input_type}
                        onChange={(e) => handleCustomColumnFormChange(
                          columnIndex as 1 | 2 | 3,
                          'input_type',
                          e.target.value as 'text' | 'dropdown'
                        )}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      >
                        <option value="text">Freeform Text</option>
                        <option value="dropdown">Dropdown (Predefined Options)</option>
                      </select>
                    </div>

                    {form.input_type === 'dropdown' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dropdown Options</label>
                        <div className="space-y-2">
                          {form.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const newOptions = [...form.options]
                                  newOptions[optionIndex] = e.target.value
                                  handleCustomColumnOptionsChange(columnIndex as 1 | 2 | 3, newOptions)
                                }}
                                placeholder={`Option ${optionIndex + 1}`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newOptions = form.options.filter((_, i) => i !== optionIndex)
                                  handleCustomColumnOptionsChange(columnIndex as 1 | 2 | 3, newOptions)
                                }}
                                className="text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const newOptions = [...form.options, '']
                              handleCustomColumnOptionsChange(columnIndex as 1 | 2 | 3, newOptions)
                            }}
                            className="text-sm text-purple-600 hover:text-purple-800"
                          >
                            + Add Option
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={() => handleSaveCustomColumn(columnIndex as 1 | 2 | 3)}
                        disabled={!form.column_title.trim()}
                        className="bg-purple-200 text-purple-950 hover:bg-purple-300 px-3 py-1 rounded text-sm font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save Column
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </InfoCard>
  )
}

